import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useDidMountEffect from '@functional/useDidMountEffect';

import Button from '@ui-elements/Button';

import { canEditSurveyStatus } from '@functional';
import HelpButton from '@ui-parts/HelpButton';
import Header from './organisms/Header';
import Quotas from './organisms/Quotas';
import DeliveryDate from './organisms/DeliveryDate';

import {
  callThunk,
  fetchSavedDeliverySettings,
  fetchEstimatedDeliveryTargetCounts,
  updateSurvey,
} from '~/api';
import { ROUTES } from '~/constants/routes';
import { PALETTE, Z_INDEX } from '~/assets/styles/base';

const HelpButtonBox = styled.div`
  position: fixed;
  top: 70%;
  right: calc(100% * 0.05);
  z-index: ${Z_INDEX.max};
`;

const ContentBox = styled.div`
  width: 100%;
  padding: 20px;
  margin: 30px 0;
  border: 1px solid ${PALETTE.border.frame};
  background: ${PALETTE.white};
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 30px;
  Button {
    height: 48px;
    transition: opacity 0.4s;
    &:hover {
      opacity: 0.8;
    }
  }
`;

const HeaderWrapper = styled.div`
  margin-top: 16px;
`;

function Survey() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // urlからidを取得
  const { id: surveyId } = useParams();

  // selector
  const { survey, surveyPayment, qualifications, quotas } = useSelector((state) => state);
  const validationClickedGoToApply = useSelector((state) => state.validation.clickedGoToApply);
  const [canEdit, setCanEdit] = useState(false);
  const headerRef = useRef(null);

  // hooks
  useEffect(() => {
    // validationエラーを全て初期化
    dispatch({
      type: 'validation/validationInitialized',
      payload: {
        eventType: 'all',
      },
    });

    // 既にstoreに保存済みのデータが存在しているので初期化しない（申し込み画面から戻ってきた時）。
    if (location.state?.referrer === ROUTES.USER.SURVEY_APPLY) {
      if (Object.keys(quotas.quotas).length === 0) {
        dispatch(
          callThunk({
            action: fetchEstimatedDeliveryTargetCounts,
            payload: { isReconstructing: true },
            wholeLoadingMessage: t('quotas.loadingMessageEstimatedDeliveryTargetCounts'),
          })
        );
      }
      return;
    }

    // 上記以外は初期化する
    dispatch({ type: 'quotas/initialize' });
    dispatch({ type: 'qualifications/initialize' });
    dispatch({ type: 'enqueteCollectCounts/initialize' });
    dispatch({ type: 'invoiceRecipient/initialize' });
    dispatch({ type: 'surveyPayment/initialize' });

    /**
     * survey/registerから遷移してきた場合は、
     * 地域とスペパネ情報取得用に保存済みアンケートデータ取得アクションをdispatchする。
     */
    if (location.state?.referrer === ROUTES.USER.SURVEY_REGISTER_GOOGLE_FORMS) {
      (async () => {
        await dispatch(
          callThunk({
            action: fetchSavedDeliverySettings,
            payload: { surveyId },
          })
        );
      })();
      return;
    }

    /**
     * mypageから遷移してきた場合やリロードの時は保存済みのアンケートデータや配信設定を取得
     */
    (async () => {
      const { payload: deliverySettings } = await dispatch(
        callThunk({
          action: fetchSavedDeliverySettings,
          payload: { surveyId },
        })
      );

      // 対象者条件設定がない、もしくは編集不可なステータスのアンケートの場合は対象者条件検索を呼ばない
      if (
        canEditSurveyStatus(deliverySettings.survey?.surveyStatusId) &&
        deliverySettings.qualifications.id !== null
      ) {
        dispatch(
          callThunk({
            action: fetchEstimatedDeliveryTargetCounts,
            payload: { isReconstructing: true },
            wholeLoadingMessage: t('quotas.loadingMessageEstimatedDeliveryTargetCounts'),
          })
        );
      }
    })();
  }, []);

  useEffect(() => {
    setCanEdit(canEditSurveyStatus(survey.surveyStatusId));
  }, [survey.surveyStatusId]);

  useEffect(() => {
    // validationが全部OKなら、申し込み画面に遷移（配信日がデフォルト値のままの場合を考慮して、API経由で配信日を保存）
    if (
      validationClickedGoToApply.status.isSubmitBtnClicked &&
      validationClickedGoToApply.status.valid
    ) {
      (async () => {
        await dispatch(
          callThunk({
            action: updateSurvey,
            payload: survey,
          })
        );
        // validationエラーを全て初期化
        dispatch({
          type: 'validation/validationInitialized',
          payload: {
            eventType: 'all',
          },
        });
        navigate(ROUTES.USER.SURVEY_APPLY.replace(':id', survey.id), {
          state: { referrer: window.location.pathname },
        });
      })();
    }
  }, [validationClickedGoToApply.status]);

  // サブミットボタンからバリデーションエラーに発生した際、画面上部の項目(surveyPrice)についてはそこまでスクロールさせる。
  useEffect(() => {
    if (
      validationClickedGoToApply.status.isSubmitBtnClicked &&
      validationClickedGoToApply.status.invalid
    ) {
      if (validationClickedGoToApply.errors.surveyPrice.invalid) {
        headerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      // 次回からrevalidateClickedGoToApplyが発火するようにisSubmitBtnClickedを元に戻す。
      dispatch({
        type: 'validation/clickedGoToApplyStatusUpdate',
        payload: {
          isSubmitBtnClicked: false,
        },
      });
    }
  }, [validationClickedGoToApply.errors]);

  // functions
  const handleClickGoToApply = async () => {
    // validationSliceでvalidationを発火させる
    dispatch({
      type: 'validation/validateClickedGoToApply',
      payload: {
        estimatePriceTotal: Number(surveyPayment.estimatePriceTotal),
        targetSamplesTotal: survey.targetSamplesTotal,
        qualifications: qualifications.current,
        quotas,
      },
    });
  };

  useDidMountEffect(() => {
    if (
      !validationClickedGoToApply.status.isSubmitBtnClicked &&
      validationClickedGoToApply.status.invalid
    ) {
      dispatch({
        type: 'validation/revalidateClickedGoToApply',
        payload: {
          estimatePriceTotal: Number(surveyPayment.estimatePriceTotal),
          targetSamplesTotal: survey.targetSamplesTotal,
          qualifications: qualifications.current,
          quotas,
        },
      });
    }
  }, [surveyPayment, survey.targetSamplesTotal, qualifications, quotas]);

  return (
    <>
      <HeaderWrapper ref={headerRef}>
        <Header survey={survey} canEdit={canEdit} />
      </HeaderWrapper>

      <ContentBox>
        <Quotas canEdit={canEdit} />
      </ContentBox>

      <ContentBox>
        <DeliveryDate canEdit={canEdit} />
      </ContentBox>

      {canEdit && (
        <ButtonWrapper>
          <Button
            variant="contained"
            colorType="yellow"
            handleClick={handleClickGoToApply}
            width="280px"
            dataTestId="GoToApply"
          >
            {t('appCommon.goToApply')}
          </Button>
        </ButtonWrapper>
      )}

      <HelpButtonBox>
        <HelpButton href={ROUTES.COMMON.HELP} />
      </HelpButtonBox>
    </>
  );
}

export default Survey;
